<script setup lang="ts">
import { computed } from "vue";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import type { Table } from "@tanstack/vue-table";

interface ServerPagination {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

interface Props {
  table?: Table<any>;
  pageSizeOptions?: number[];
  serverPagination?: ServerPagination;
}

const props = withDefaults(defineProps<Props>(), {
  pageSizeOptions: () => [25, 50, 100],
  serverPagination: undefined,
  table: undefined,
});

const emit = defineEmits<{
  (e: "update:page-size", value: number): void;
  (e: "update:current-page", value: number): void;
}>();

const currentPage = computed({
  get: () => (props.table ? props.table.getState().pagination.pageIndex + 1 : props.serverPagination?.currentPage),
  set: (value: number) => {
    if (props.table) {
      return props.table.setPageIndex(value - 1);
    }
    emit("update:current-page", value);
  },
});

const totalPages = computed(() => (props.table ? props.table.getPageCount() : props.serverPagination?.totalPages));

const totalRecords = computed(() =>
  props.table ? props.table.getFilteredRowModel().rows.length : props.serverPagination?.totalCount,
);

const pageSize = computed(() =>
  props.table ? props.table.getState().pagination.pageSize : props.serverPagination?.pageSize,
);

const fromCount = computed(() => {
  if (totalRecords.value === 0) return 0;
  return (currentPage.value! - 1) * pageSize.value! + 1;
});

const toCount = computed(() => {
  if (totalRecords.value === 0) return 0;
  const end = currentPage.value! * pageSize.value!;
  return end > totalRecords.value! ? totalRecords.value! : end;
});

function handlePageSizeChange(value: unknown) {
  if (props.table) {
    return props.table.setPageSize(Number(value));
  }
  emit("update:page-size", Number(value));
}
</script>

<template>
  <div class="flex w-full items-center justify-between gap-6 rounded-b-md bg-white p-3">
    <p class="flex-1 text-sm">Showing {{ fromCount }} to {{ toCount }} of {{ totalRecords }} result(s).</p>
    <div class="flex items-center gap-6">
      <div class="flex items-center gap-2">
        <Label for="table_page_size" class="whitespace-nowrap">Results per page</Label>
        <Select :model-value="pageSize" @update:model-value="handlePageSizeChange">
          <SelectTrigger class="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="option in pageSizeOptions" :key="option" :value="option">
              {{ option }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p class="text-sm whitespace-nowrap" :class="{ 'text-muted-foreground opacity-90': !totalPages }">
        <span v-if="totalPages"> Page {{ currentPage }} of {{ totalPages }} </span>
        <span v-else>No results</span>
      </p>
      <Pagination
        v-model:page="currentPage"
        :items-per-page="pageSize!"
        :total="totalRecords"
        :default-page="currentPage"
        :disabled="totalPages! < 2"
        :show-edges="true"
        :sibling-count="1"
      >
        <PaginationContent v-slot="{ items }">
          <PaginationFirst />
          <PaginationPrevious />
          <template v-for="(item, idx) in items" :key="idx">
            <PaginationItem v-if="item.type === 'page'" :value="item.value" :is-active="item.value === currentPage">
              {{ item.value }}
            </PaginationItem>
            <PaginationEllipsis v-else />
          </template>
          <PaginationNext />
          <PaginationLast />
        </PaginationContent>
      </Pagination>
    </div>
  </div>
</template>
